import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TableCell from 'src/components/Table/TableCell';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import selectFormatDate from 'src/utils/selectDateFormat';
import dateFormatType from 'src/utils/dateFormatType';
import StatusIcon from 'src/components/StatusIcon';
import { AmendingAgreementSignStatus } from 'src/utils/constants/contracts';
import IconStatusAndColor from 'src/utils/constants/common';
import MoreMenu from 'src/components/MoreMenu/MoreMenu';
import { CapitalContributionActions } from 'src/utils/constants/clients';
import DocumentViewerDialog from 'src/components/DocumentViewer/DocumentViewerDialog';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import { useTranslation } from 'react-i18next';

const getStatusIcon = status => {
  switch (status) {
    case AmendingAgreementSignStatus.SIGNED:
      return (
        <StatusIcon
          description="CONTRACT_VIEW.PAYMENT_COMPLETED"
          type={IconStatusAndColor.OK.status}
        />
      );
    case AmendingAgreementSignStatus.PENDING_SIGNATURE:
      return (
        <StatusIcon
          description="COMMON.PENDING"
          type={IconStatusAndColor.WARNING.status}
        />
      );

    default:
      return (
        <StatusIcon
          description="COMMON.NONE"
          type={IconStatusAndColor.NONE.status}
        />
      );
  }
};

const ContributionsRow = ({
  date,
  amount,
  iva,
  amountWithIva,
  signStatus,
  document,
  invoiceDoc,
  terminationLetter
}) => {
  const [openOption, setOpenOption] = useState('');
  const { t } = useTranslation();

  const handleOpenDocumentView = () => {
    setOpenOption(CapitalContributionActions.VIEW_DOCUMENT);
  };

  const handleOpenInvoiceView = () => {
    setOpenOption(CapitalContributionActions.VIEW_INVOICE);
  };

  const handleOpenTerminationLetter = () => {
    setOpenOption(CapitalContributionActions.VIEW_TERMINATION_LETTER);
  };

  const handleCloseDialog = () => {
    setOpenOption('');
  };

  const handleClickMore = action => {
    switch (action) {
      case CapitalContributionActions.VIEW_DOCUMENT:
        handleOpenDocumentView();
        break;
      case CapitalContributionActions.VIEW_INVOICE:
        handleOpenInvoiceView();
        break;
      case CapitalContributionActions.VIEW_TERMINATION_LETTER:
        handleOpenTerminationLetter();
        break;
      default:
        break;
    }
  };

  const getMoreMenuOptions = useCallback(() => {
    const options = [];
    if (document) {
      options.push({
        name: CapitalContributionActions.VIEW_DOCUMENT,
        label: 'COMMON.VIEW_AMENDING_AGREEMENT',
        icon: <InsertDriveFileRoundedIcon />
      });
    }

    if (invoiceDoc) {
      options.push({
        name: CapitalContributionActions.VIEW_INVOICE,
        label: 'COMMON.VIEW_INVOICE',
        icon: <InsertDriveFileRoundedIcon />
      });
    }

    if (terminationLetter) {
      options.push({
        name: CapitalContributionActions.VIEW_TERMINATION_LETTER,
        label: 'COMMON.TERMINATION_LETTER',
        icon: <InsertDriveFileRoundedIcon />
      });
    }

    return options;
  }, [document, invoiceDoc, terminationLetter]);

  return (
    <TableRow hover>
      <TableCell align="center" width="15%">
        {selectFormatDate(date, dateFormatType.DAY)}
      </TableCell>
      <TableCell align="center" width="20%">
        {formatAmountWithDecimals(amount)}
      </TableCell>
      <TableCell align="center" width="20%">
        {formatAmountWithDecimals(iva)}
      </TableCell>
      <TableCell align="center" width="20%">
        {formatAmountWithDecimals(amountWithIva)}
      </TableCell>
      <TableCell align="center" width="20%">
        {getStatusIcon(signStatus)}
      </TableCell>
      <TableCell align="center" width="20%">
        <MoreMenu
          options={getMoreMenuOptions()}
          handleClickOption={handleClickMore}
          deleteDisabled
          editDisabled
        />
        {document && (
          <DocumentViewerDialog
            documentUrl={document.documentUrl}
            open={openOption === CapitalContributionActions.VIEW_DOCUMENT}
            onClose={handleCloseDialog}
            fileName={t('COMMON.AMENDING_AGREEMENT')}
          />
        )}
        {invoiceDoc && (
          <DocumentViewerDialog
            documentUrl={invoiceDoc.documentUrl}
            open={openOption === CapitalContributionActions.VIEW_INVOICE}
            onClose={handleCloseDialog}
            fileName={t('COMMON.INVOICE')}
          />
        )}
        {terminationLetter && (
          <DocumentViewerDialog
            documentUrl={terminationLetter.documentUrl}
            open={
              openOption === CapitalContributionActions.VIEW_TERMINATION_LETTER
            }
            onClose={handleCloseDialog}
            fileName="Carta de terminación"
          />
        )}
      </TableCell>
    </TableRow>
  );
};

ContributionsRow.propTypes = {
  date: PropTypes.string,
  amount: PropTypes.number,
  iva: PropTypes.number,
  amountWithIva: PropTypes.number,
  signStatus: PropTypes.string,
  document: PropTypes.object,
  invoiceDoc: PropTypes.object,
  terminationLetter: PropTypes.object
};

export default ContributionsRow;
